import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { AlertCircle, DollarSign, Gift } from "lucide-react";
import {
  useIssueCustomerCredit,
  type CustomerCreditKind,
  type CustomerCreditScope,
} from "@/api/customer-credits";
import { useGetCarriers } from "@/api/carriers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface IssueVoucherDialogProps {
  customerId: string;
  customerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * #262 — admin "Issue voucher" dialog. Wraps POST /api/v2/admin/customers/:id/credits.
 *
 * Voucher = wallet item (free pickup, $X off). Distinct from the legacy
 * CustomerBalance "credit" deposit (which is currency-on-account).
 */
export function IssueVoucherDialog({
  customerId,
  customerName,
  open,
  onOpenChange,
}: IssueVoucherDialogProps) {
  const [kind, setKind] = useState<CustomerCreditKind>("FREE_LINE_ITEM");
  const [scope, setScope] = useState<CustomerCreditScope>("PICKUP");
  const [amountDollars, setAmountDollars] = useState("");
  const [reason, setReason] = useState("");
  const [expiryDays, setExpiryDays] = useState("90");
  const [carrierId, setCarrierId] = useState("");

  const issue = useIssueCustomerCredit(customerId);
  const { data: carriersResult, isLoading: carriersLoading } = useGetCarriers({
    active: true,
  });
  const carriers = carriersResult?.data ?? [];

  useEffect(() => {
    if (open && !carrierId && carriers.length > 0) {
      setCarrierId(carriers[0].id);
    }
  }, [carrierId, carriers, open]);

  function reset() {
    setKind("FREE_LINE_ITEM");
    setScope("PICKUP");
    setAmountDollars("");
    setReason("");
    setExpiryDays("90");
    setCarrierId("");
  }

  function handleSubmit() {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Reason required");
      return;
    }
    if (!carrierId) {
      toast.error("Carrier required");
      return;
    }

    let amountCents: number | undefined;
    if (kind === "AMOUNT") {
      const n = parseFloat(amountDollars);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Enter a positive amount");
        return;
      }
      amountCents = Math.round(n * 100);
    }

    const validUntil = new Date(
      Date.now() + parseInt(expiryDays, 10) * 24 * 60 * 60 * 1000
    ).toISOString();

    issue.mutate(
      {
        carrierId,
        kind,
        scope: kind === "FREE_LINE_ITEM" ? scope : undefined,
        amountCents,
        reason: trimmedReason,
        validUntil,
      },
      {
        onSuccess: () => {
          toast.success("Voucher issued", {
            description: `Issued to ${customerName}`,
          });
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error("Failed to issue voucher", { description: err.message });
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Issue voucher to {customerName}</DialogTitle>
          <DialogDescription>
            Auto-redeems on the customer's next eligible order. Vouchers are
            immutable once issued.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-medium">Carrier</Label>
            <Select
              value={carrierId}
              onValueChange={setCarrierId}
              disabled={carriersLoading}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select carrier" />
              </SelectTrigger>
              <SelectContent>
                {carriers.map((carrier) => (
                  <SelectItem key={carrier.id} value={carrier.id}>
                    {carrier.name} ({carrier.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium">Kind</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                onClick={() => setKind("AMOUNT")}
                className={`border rounded-md p-3 text-left transition ${
                  kind === "AMOUNT"
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <DollarSign className="h-5 w-5 text-gray-600 mb-1" />
                <div className="font-medium text-sm">Amount off</div>
                <div className="text-xs text-muted-foreground">
                  Specific $ off any order subtotal
                </div>
              </button>
              <button
                type="button"
                onClick={() => setKind("FREE_LINE_ITEM")}
                className={`border rounded-md p-3 text-left transition ${
                  kind === "FREE_LINE_ITEM"
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Gift className="h-5 w-5 text-gray-600 mb-1" />
                <div className="font-medium text-sm">Free line item</div>
                <div className="text-xs text-muted-foreground">
                  Free pickup or delivery on next order
                </div>
              </button>
            </div>
          </div>

          {kind === "AMOUNT" ? (
            <div>
              <Label htmlFor="amount" className="text-sm">
                Amount ($CAD)
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amountDollars}
                onChange={(e) => setAmountDollars(e.target.value)}
                placeholder="5.00"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Applied off the order subtotal at checkout (after bundle and
                tier discounts).
              </p>
            </div>
          ) : (
            <div className="border rounded-md p-3">
              <Label className="text-sm font-medium">Scope</Label>
              <RadioGroup
                value={scope}
                onValueChange={(v) => setScope(v as CustomerCreditScope)}
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="PICKUP" id="scope-pickup" />
                  <Label htmlFor="scope-pickup" className="cursor-pointer">
                    Pickup
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="DELIVERY" id="scope-delivery" />
                  <Label htmlFor="scope-delivery" className="cursor-pointer">
                    Delivery
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <div>
            <Label htmlFor="reason" className="text-sm">
              Reason
            </Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Service recovery — late delivery on order #4521"
              className="mt-1"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Visible to the customer on their voucher card.
            </p>
          </div>

          <div>
            <Label className="text-sm font-medium">Expires</Label>
            <RadioGroup
              value={expiryDays}
              onValueChange={setExpiryDays}
              className="grid grid-cols-3 gap-2 mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="30" id="exp-30" />
                <Label htmlFor="exp-30" className="cursor-pointer text-sm">
                  30 days
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="90" id="exp-90" />
                <Label htmlFor="exp-90" className="cursor-pointer text-sm">
                  90 days
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="365" id="exp-365" />
                <Label htmlFor="exp-365" className="cursor-pointer text-sm">
                  1 year
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              Once issued, a voucher cannot be edited or deleted. It expires on
              its set date or is consumed on the customer's next eligible order.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={issue.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={issue.isPending}>
            {issue.isPending ? "Issuing..." : "Issue voucher"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
